import { Directive, HostListener, OnInit, OnChanges, OnDestroy, SimpleChanges, Input, ElementRef, Output, EventEmitter, inject } from '@angular/core';
import { Subscription } from 'rxjs';

import { HisTranslationService } from '../service/his-translation.service';
import { HisDdDirective } from '../../core/directive/his-dd.directive';
import { Language } from '../model/language';
import { HisSummernoteComponent } from '../../core/component/summernote/his-summernote.component';

/**
 */
@Directive( {
    selector: '[hisObject]',
    standalone: false
} )
export class HisInputTranslationDirective implements OnInit, OnChanges, OnDestroy {
    private elementRef = inject(ElementRef);
    private hisTranslationService = inject(HisTranslationService);
    private ddDirective = inject(HisDdDirective);
    private hisEditorComponent = inject( HisSummernoteComponent, { host: true, optional: true, self: true });


    /** object we are translating */
    @Input() hisObject: any;

    /** attribute name we are translating */
    @Input() hisAttribute: string;
    
    @Output() translationLanguageChanged = new EventEmitter<Language>();

    private translationSubscription: Subscription;
    private hisEditorSubscription: Subscription;

    ngOnInit(): void {
        if(this.hisEditorComponent) {
            (this.hisEditorComponent as any).nativeElement = this.elementRef.nativeElement;
            this.hisEditorSubscription = this.hisEditorComponent.dataChange.subscribe( (data) => {
                this.hisTranslationService.setHisObjectActiveAttribute( this.hisObject, this.hisAttribute, this.hisEditorComponent.data );
            });
        }
        this.elementRef.nativeElement.isTranslatable = true;
        // listen to changes on the translationLanguage
        this.translationSubscription = this.hisTranslationService.translationLanguage$
            .subscribe( (newTranslationLanguage: Language) => {
                this.onTranslationLanguageChanged( newTranslationLanguage );
            } );
    }

    ngOnChanges( changes: SimpleChanges ): void {
        if ( this.hisObject && this.hisAttribute ) {
            // if the value of the given attribute change, pass the value to the element the directive is applied to
            this.updateElementValue();
        }
    }

    ngOnDestroy() {
        if (this.translationSubscription) {
            this.translationSubscription.unsubscribe();
            this.onTranslationLanguageChanged( undefined );
        }
        if(this.hisEditorSubscription) {
            this.hisEditorSubscription.unsubscribe();
        }
    }

    /** host listener sets the value of the hisObject[hisAttribute] when input is given */
    @HostListener( 'input')
    onInput() {
        if(!this.hisEditorComponent) {
            this.hisTranslationService.setHisObjectActiveAttribute( this.hisObject, this.hisAttribute, this.elementRef.nativeElement.value );
        }
    }

    /** Updates the value of the element the directive is applied to */
    private updateElementValue() {
        // set the defaultLanguageValue (his-plam will read it)
        this.elementRef.nativeElement.defaultLanguageValue = this.hisObject[this.hisAttribute];
        // set value for current language field
        if(this.hisEditorComponent) {
            this.hisEditorComponent.writeValue(this.hisTranslationService.getHisObjectActiveAttribute( this.hisObject, this.hisAttribute ));
        } else  {
            this.elementRef.nativeElement.value = this.hisTranslationService.getHisObjectActiveAttribute( this.hisObject, this.hisAttribute );
        }
    }

    private onTranslationLanguageChanged( newTranslationLanguage: Language ) {
        // disabled ddProperty required for the translation input
        if ( newTranslationLanguage ) {
            this.ddDirective.forceNotRequired( true );
        } else {
            this.ddDirective.forceNotRequired( false );
        }
        
        this.translationLanguageChanged?.emit(newTranslationLanguage);
        this.updateElementValue();
    }
}
